import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { spacing, ticketColors, ticketFonts } from '../../shared/theme/public-api';
import { typography } from '../../shared/theme/typography';
import { fetchProductCombos } from '../infra/productCombosRepo';
import type { ProductCombo } from '../domain/productCombos';

const PREVIEW_MAX = 3;

type Props = {
  productId: string;
  onViewComposition: () => void;
};

export function CatalogProductDetailCompositionSummary({ productId, onViewComposition }: Props) {
  const [combo, setCombo] = useState<ProductCombo | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchProductCombos([productId]).then((map) => {
      if (!cancelled) setCombo(map.get(productId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const parts = combo?.components ?? [];
  if (parts.length === 0) return null;

  const preview = parts.slice(0, PREVIEW_MAX);
  const rest = parts.length - preview.length;

  return (
    <View style={styles.wrap} testID="catalog.detail.composition-summary">
      <View style={styles.headingRow}>
        <Text style={styles.title}>Partes del combo</Text>
        <TouchableOpacity
          onPress={onViewComposition}
          accessibilityRole="button"
          accessibilityLabel="Ver composición completa"
          testID="catalog.detail.composition-summary.link"
        >
          <Text style={styles.link}>Ver composición</Text>
        </TouchableOpacity>
      </View>
      {preview.map((c) => (
        <Text key={`${c.productId}-${c.sortOrder}`} style={styles.line} numberOfLines={1}>
          {c.qty}× {c.name ?? 'Producto'}
        </Text>
      ))}
      {rest > 0 ? (
        <Text style={styles.more}>
          y {rest} más —{' '}
          <Text style={styles.linkInline} onPress={onViewComposition}>
            ver todo
          </Text>
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.border,
  },
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  link: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.brandPrimary,
  },
  linkInline: {
    color: ticketColors.brandPrimary,
    fontWeight: '600',
  },
  line: {
    ...typography.body,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
  },
  more: {
    ...typography.caption,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink3,
    marginTop: 2,
  },
});
